import { fetchAndSumBalanceHistory, fetchKasAddedHistory, fetchUsdcBalanceHistory } from './miners/KaspaBalanceChart';
import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import wallets from '../assets/wallets.json';
import DashboardTab from './miners/DashboardTab';
import DetailsTab from './miners/DetailsTab';
import FinanceTab from './miners/FinanceTab';

const CURRENT_BALANCE_ADDRESSES = wallets.KaspaBalanceAddresses;
const MINING_BALANCE_CACHE_KEY = `jjphotos:mining-wallet-balance:v1:${CURRENT_BALANCE_ADDRESSES.map(address => address.toLowerCase()).sort().join('|')}`;
const readCachedMiningTotal = () => {
    try {
        const cachedValue = localStorage.getItem(MINING_BALANCE_CACHE_KEY);
        if (cachedValue === null) return null;
        const value = Number(cachedValue);
        return Number.isFinite(value) && value >= 0 ? value : null;
    } catch {
        return null;
    }
};



const Mine = () => {
        // --- Worker Finance State (for Finance tab) ---
        const [workerFinance, setWorkerFinance] = React.useState(null);
        React.useEffect(() => {
            import('../assets/workerFinance.json').then(mod => setWorkerFinance(mod.default || mod));
        }, []);
    const [userData, setUserData] = useState(null);
    const [outstandingPowerpoolBalance, setOutstandingPowerpoolBalance] = useState(null);
    const [poolData, setPoolData] = useState(null);
    const [k1PoolData, setK1PoolData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [initialLoad, setInitialLoad] = useState(true);
    const [error, setError] = useState(null);
    const [timeUntilRefresh, setTimeUntilRefresh] = useState(300); // 5 minutes in seconds
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [period, setPeriod] = useState('week');
    const [fetchedData, setFetchedData] = useState({});
    const [activeTab, setActiveTab] = useState('dashboard');
    const [kasPrice, setKasPrice] = useState(null);
    const [priceLoading, setPriceLoading] = useState(true);
    const [priceError, setPriceError] = useState(null);
    const [kasHigh24h, setKasHigh24h] = useState(null);
    const [kasLow24h, setKasLow24h] = useState(null);
    const [kasChange24h, setKasChange24h] = useState(null);
    const [kasPriceHistory, setKasPriceHistory] = useState([]);
    const [pricePeriod, setPricePeriod] = useState('day');
    
    // --- RustyKaspa state ---
    const [kaspaData, setKaspaData] = useState(null);
    const [kaspaError, setKaspaError] = useState(null);
    const [kaspaFetchTime, setKaspaFetchTime] = useState(null);

    const [usdToGbpRate, setUsdToGbpRate] = useState(0.79);

    // Fetch USD to GBP rate from an API
    const fetchUsdToGbpRate = async () => {
        try {
            // Example using exchangerate.host (free, no API key required)
            const response = await fetch('https://api.exchangerate.host/latest?base=USD&symbols=GBP');
            const data = await response.json();
            if (data && data.rates && data.rates.GBP) {
                setUsdToGbpRate(data.rates.GBP);
            }
        } catch (err) {
            // If fetch fails, keep the last known rate
            console.error('Failed to fetch USD to GBP rate:', err);
        }
    };

    // Fetch on mount and every 2 hours
    useEffect(() => {
        fetchUsdToGbpRate();
        const interval = setInterval(fetchUsdToGbpRate, 2 * 60 * 60 * 1000); // 2 hours
        return () => clearInterval(interval);
    }, []);

    const API_KEY = '16f87506ffc84352ab04b6b164f476ec';
    const REFRESH_INTERVAL = 300; // 5 minutes in seconds

    const fetchUserData = async () => {
        try {
            if (initialLoad) setLoading(true);
            const timestamp = Date.now();
            // Use the React dev proxy locally, Netlify function in production
            const isLocal = ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname) || window.location.hostname.endsWith('.local');
            const fetchUrl = isLocal
                ? `/api-local/powerpool/user?apiKey=${API_KEY}&_t=${timestamp}`
                : '/.netlify/functions/powerpool-user';

            const response = await fetch(fetchUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
                cache: 'no-store'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log('API Response:', result);

            // Data is nested under the username key (e.g., "jjphotos")
            const userKey = Object.keys(result)[0];
            setUserData(result[userKey]);

            // If you need to calculate outstandingPowerpoolBalance, do so without mutating payments structure
            const payments = Array.isArray(result[userKey]?.payments)
                ? result[userKey].payments
                : [];
            const paymentsNoTx = payments.length > 0
                ? payments.filter(p => !p.txid && typeof p.value === 'number').reduce((sum, p) => sum + p.value, 0)
                : 0;
            const kasBalance = result[userKey]?.balances?.find(b => b.coinTicker === 'KAS');
            const poolBalance = kasBalance?.balance;
            setOutstandingPowerpoolBalance(paymentsNoTx + poolBalance);
            setError(null);
        } catch (err) {
            console.error('Error fetching mining data:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchPoolData = async () => {
        try {
            const timestamp = Date.now();
            // Use the React dev proxy locally, Netlify function in production
            const isLocal = ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname) || window.location.hostname.endsWith('.local');
            const fetchUrl = isLocal
                ? `/api-local/powerpool/pool?_t=${timestamp}`
                : '/.netlify/functions/powerpool-pool';

            const response = await fetch(fetchUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
                cache: 'no-store'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log('Pool API Response:', result);
            setPoolData(result);
        } catch (err) {
            console.error('Error fetching pool data:', err);
        }
    };

    /* const fetchK1PoolData = async () => {
        try {
            const accountId = 'KrJxvkR1cE683B99ArHLxx36snifu2zGctc';
            const isLocal = ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname) || window.location.hostname.endsWith('.local');
            const fetchUrl = isLocal
                ? `/api-local/k1pool/miner/kaspa/${accountId}`
                : '/.netlify/functions/k1pool-miner';
            const response = await fetch(fetchUrl, {
                headers: { Accept: 'application/json' },
                cache: 'no-store'
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            setK1PoolData(await response.json());
        } catch (err) {
            console.error('Error fetching K1Pool data:', err);
            setK1PoolData(null);
        }
    }; */

    const [ledgerTotal, setLedgerTotal] = useState(null);
    const [miningTotal, setMiningTotal] = useState(readCachedMiningTotal);
    const [miningBalanceLoading, setMiningBalanceLoading] = useState(true);
    const [miningBalanceError, setMiningBalanceError] = useState(null);
    const [miningLastRefresh, setMiningLastRefresh] = useState(0);
    const [ledgerLoaded, setLedgerLoaded] = useState(false);
    const [balanceHistory, setBalanceHistory] = useState([]);
    const [balanceHistoryLoading, setBalanceHistoryLoading] = useState(false);
    const [balanceHistoryError, setBalanceHistoryError] = useState(null);
    const [kasAddedHistory, setKasAddedHistory] = useState([]);
    const [kasAddedLoading, setKasAddedLoading] = useState(false);
    const [kasAddedError, setKasAddedError] = useState(null);
    const [networkHashrateHistory, setNetworkHashrateHistory] = useState([]);

    // Helper to fetch and sum balances for a list of addresses
    const fetchWalletBalances = async (addresses, onProgress, { retryDelayMs = 0, maxRetries = 0 } = {}) => {
        let total = 0;
        let successfulCount = 0;
        let failedCount = 0;
        const failures = [];
        await Promise.all(addresses.map(async address => {
            const fetchBalance = async () => {
                const controller = new AbortController();
                const timeout = setTimeout(() => controller.abort(), 10000);
                try {
                    const apiUrl = `https://api.kaspa.org/addresses/${encodeURIComponent(address)}/balance`;
                    const response = await fetch(apiUrl, { cache: 'no-store', signal: controller.signal });
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);

                    const data = await response.json();
                    const balance = Number(data?.balance);
                    if (!Number.isFinite(balance)) throw new Error('Invalid balance response');
                    return balance / 1e8;
                } finally {
                    clearTimeout(timeout);
                }
            };

            let balance;
            let lastError;
            for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
                if (attempt > 0) {
                    console.warn(`[Mining wallet balance] Retrying failed address in ${retryDelayMs / 1000}s (attempt ${attempt + 1}/${maxRetries + 1}):`, address);
                    await new Promise(resolve => setTimeout(resolve, retryDelayMs));
                }
                try {
                    balance = await fetchBalance();
                    lastError = null;
                    break;
                } catch (error) {
                    lastError = error;
                }
            }

            if (lastError) {
                failedCount += 1;
                failures.push({
                    address,
                    reason: lastError.name === 'AbortError' ? 'Request timed out after 10 seconds (including retry)' : lastError.message,
                });
            } else {
                total += balance;
                successfulCount += 1;
            }
            onProgress?.({ total, successfulCount, failedCount, failures: [...failures] });
        }));
        return { total, successfulCount, failedCount, failures };
    };

    const refreshMiningWalletBalances = async () => {
        setMiningBalanceLoading(true);
        try {
            const mining = await fetchWalletBalances(CURRENT_BALANCE_ADDRESSES, result => {
                if (result.successfulCount > 0) setMiningTotal(result.total);
            }, { retryDelayMs: 15000, maxRetries: 1 });
            setMiningTotal(mining.successfulCount > 0 ? mining.total : null);
            if (mining.failedCount > 0) {
                console.warn('[Mining wallet balance] Failed address requests:', mining.failures);
            }
            if (mining.successfulCount > 0) {
                try {
                    localStorage.setItem(MINING_BALANCE_CACHE_KEY, String(mining.total));
                } catch {
                    // Keep the live total even if browser storage is unavailable.
                }
            }
            setMiningBalanceError(mining.failedCount === CURRENT_BALANCE_ADDRESSES.length
                ? 'Unable to load mining wallet balances.'
                : mining.failedCount > 0
                    ? `Loaded ${mining.successfulCount} of ${CURRENT_BALANCE_ADDRESSES.length} mining wallet balances.`
                    : null);
            setMiningLastRefresh(Date.now());
        } catch (balanceError) {
            console.error('Error refreshing mining wallet balances:', balanceError);
            setMiningTotal(null);
            setMiningBalanceError(balanceError.message || 'Unable to load mining wallet balances.');
        } finally {
            setMiningBalanceLoading(false);
        }
    };

    // Helper to fetch and aggregate balance history for multiple addresses using UTXO logic
    // Uses the same logic as KaspaBalanceChart, but for multiple addresses

    // Fetch and sum balance history for all mining wallet addresses
    const fetchBalanceHistory = async (addresses) => {
        const ETHERSCAN_API_KEY = 'PCES6X3QI3B2VJ85WNUFQW6BSI5Q971NQP';
        const USDC_WALLET = '0x58d4BE4595Bca7293B44f9516802479cCdE04BFd';

        setBalanceHistoryLoading(true);
        setBalanceHistoryError(null);
        try {
            const { history: kasHistory, failedCount, totalAddresses } = await fetchAndSumBalanceHistory(addresses);
            const usdcHistory = await fetchUsdcBalanceHistory(USDC_WALLET, ETHERSCAN_API_KEY);
            const usdcByDate = new Map(usdcHistory.map(item => [item.date, item.usdcBalance]));

            // Merge KAS and USDC balance data by date
            const merged = kasHistory.map(kas => ({
                ...kas,
                usdcBalance: usdcByDate.get(kas.date) || 0,
            }));

            setBalanceHistory(merged);
            if (failedCount > 0) {
                setBalanceHistoryError(`Showing partial mining wallet history: ${failedCount} of ${totalAddresses} wallet address requests failed.`);
            }
        } catch (historyError) {
            console.error('Error fetching mining wallet balance history:', historyError);
            setBalanceHistory([]);
            setBalanceHistoryError(historyError.message || 'Unable to load mining wallet history.');
        } finally {
            setBalanceHistoryLoading(false);
        }
    };

    const fetchMiningWalletAddedHistory = async (addresses) => {
        setKasAddedLoading(true);
        setKasAddedError(null);
        try {
            const result = await fetchKasAddedHistory(addresses);
            setKasAddedHistory(result.history);
            if (result.failedCount > 0) {
                setKasAddedError(`Showing partial confirmed-transaction totals: ${result.failedCount} of ${result.totalAddresses} wallet address requests failed.`);
            }
        } catch (historyError) {
            console.error('Error fetching confirmed mining wallet transactions:', historyError);
            setKasAddedHistory([]);
            setKasAddedError(historyError.message || 'Unable to load confirmed mining wallet transactions.');
        } finally {
            setKasAddedLoading(false);
        }
    };

    // Helper to fetch network hashrate history
    const fetchNetworkHashrateHistory = async () => {
        const days = period === 'day' ? 1 : period === 'week' ? 7 : period === 'month' ? 30 : 90; // Limit year to 90 for performance
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];
        const datesToFetch = [];
        for (let i = days - 1; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(date.getDate() - i);
            const dateStr = date.toISOString().split('T')[0];
            datesToFetch.push(dateStr);
        }
        // Fetch all missing dates in parallel
        const fetchPromises = datesToFetch.map(async (dateStr) => {
            if (fetchedData[dateStr] && dateStr !== todayStr) return;
            try {
                const apiUrl = `https://api.kaspa.org/info/hashrate/history/${dateStr}?resolution=15m`;
                const resp = await fetch(apiUrl);
                if (resp.ok) {
                    const data = await resp.json();
                    if (Array.isArray(data)) {
                        return { dateStr, data: data.map(point => ({
                            timestamp: point.timestamp,
                            hashrate: point.hashrate_kh * 1000
                        })) };
                    }
                }
            } catch (e) {
                // ignore
            }
            return null;
        });
        const results = await Promise.all(fetchPromises);
        const newFetchedData = { ...fetchedData };
        results.forEach(result => {
            if (result) {
                newFetchedData[result.dateStr] = result.data;
            }
        });
        setFetchedData(newFetchedData);
        // Collect all data
        const history = [];
        datesToFetch.forEach(dateStr => {
            if (newFetchedData[dateStr]) {
                history.push(...newFetchedData[dateStr]);
            }
        });
        // Sort by timestamp
        history.sort((a, b) => a.timestamp - b.timestamp);
        // Filter to last period
        const now = Date.now();
        const periodMs = period === 'day' ? 24 * 60 * 60 * 1000 : period === 'week' ? 7 * 24 * 60 * 60 * 1000 : period === 'month' ? 30 * 24 * 60 * 60 * 1000 : 90 * 24 * 60 * 60 * 1000;
        const filteredHistory = history.filter(point => point.timestamp > now - periodMs);
        setNetworkHashrateHistory(filteredHistory);
    };

    // Refetch network hashrate when period changes
    useEffect(() => {
        fetchNetworkHashrateHistory();
    }, [period]);

    // Refetch price history when pricePeriod changes
    useEffect(() => {
        fetchKasPriceHistory();
    }, [pricePeriod]);

    useEffect(() => {
        const doInitialFetch = async () => {
            // Start the current mining balance first; it does not depend on transaction history.
            refreshMiningWalletBalances();
            // Load chart histories independently in the background.
            fetchBalanceHistory(wallets.MiningWallet);
            fetchMiningWalletAddedHistory(wallets.MiningWallet.slice(0, 1));
            await fetchUserData();
            setInitialLoad(false);
            fetchPoolData();
            fetchRustyKaspaData();
            // Only fetch cold wallet once per session
            if (!ledgerLoaded) {
                const ledger = await fetchWalletBalances(wallets.ColdWallet);
                setLedgerTotal(ledger.total);
                setLedgerLoaded(true);
            }
            // Fetch network hashrate history
            fetchNetworkHashrateHistory();
            // Fetch KAS price
            fetchKasPrice();
            // Fetch KAS price history
            fetchKasPriceHistory();
        };
        doInitialFetch();
        // Refresh mining wallet every 2 hours
        const miningInterval = setInterval(refreshMiningWalletBalances, 2 * 60 * 60 * 1000); // 2 hours
        // Refresh user/pool data every 5 minutes
        const interval = setInterval(() => {
            fetchUserData();
            fetchPoolData();
            fetchNetworkHashrateHistory();
            fetchRustyKaspaData();
            setTimeUntilRefresh(REFRESH_INTERVAL);
        }, REFRESH_INTERVAL * 1000);
        // Refresh KAS price every 60 seconds
        const priceInterval = setInterval(fetchKasPrice, 60 * 1000);
        // Refresh KAS price history every 2 hours
        const priceHistoryInterval = setInterval(fetchKasPriceHistory, 2 * 60 * 60 * 1000);
        return () => {
            clearInterval(interval);
            clearInterval(miningInterval);
            clearInterval(priceInterval);
            clearInterval(priceHistoryInterval);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchKasPrice = async () => {
        try {
            setPriceLoading(true);
            const isLocal = ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname) || window.location.hostname.endsWith('.local');
            const fetchUrl = isLocal
                ? '/api-local/coingecko/coins/kaspa?localization=false&tickers=false&market_data=true&community_data=false&developer_data=false&sparkline=false'
                : '/.netlify/functions/kas-price';
            const response = await fetch(fetchUrl);
            if (!response.ok) throw new Error('Failed to fetch KAS price');
            const data = await response.json();
            const price = isLocal
                ? data.market_data?.current_price?.usd
                : data.kaspa?.usd;
            if (typeof price !== 'number') throw new Error('KAS price missing from response');
            setKasPrice(price);
            setKasHigh24h(isLocal ? data.market_data?.high_24h?.usd ?? null : data.high_24h ?? null);
            setKasLow24h(isLocal ? data.market_data?.low_24h?.usd ?? null : data.low_24h ?? null);
            setKasChange24h(isLocal ? data.market_data?.price_change_percentage_24h ?? null : data.percent_change_24h ?? null);
            setPriceError(null);
        } catch (err) {
            setPriceError(err.message);
        } finally {
            setPriceLoading(false);
        }
    };

    const fetchKasPriceHistory = async () => {
        try {
            if (process.env.NODE_ENV === 'development') {
                // Load sample data in development
                const response = await fetch('/kas-price-history-sample.json');
                const data = await response.json();
                setKasPriceHistory(data);
            } else {
                // Fetch from API in production
                const days = pricePeriod === 'day' ? 1 : pricePeriod === 'week' ? 7 : pricePeriod === 'month' ? 30 : 365;
                const response = await fetch(`/.netlify/functions/kas-price-history?days=${days}`);
                if (!response.ok) throw new Error('Failed to fetch KAS price history');
                const data = await response.json();
                const history = data.prices.map(([timestamp, price]) => ({
                    timestamp,
                    price
                }));
                setKasPriceHistory(history);
                console.log('Price history data:', JSON.stringify(history));
            }
        } catch (err) {
            console.error('Error fetching KAS price history:', err);
        }
    };

    // Fetch RustyKaspa data
    const fetchRustyKaspaData = async () => {
        try {
            setKaspaError(null);
            const walletAddress = 'kaspa:qzcakgulrnw4tzmzuny32rg3ddlujqfz0q2c9f4x8qzl6y4tukc7cjjhfknag';
            const isLocal = ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname) || window.location.hostname.endsWith('.local');
            const fetchUrl = isLocal
                ? `/api-local/rustykaspa?wallet=${encodeURIComponent(walletAddress)}`
                : `/.netlify/functions/rustykaspa-search?walletAddress=${encodeURIComponent(walletAddress)}`;
            const resp = await fetch(fetchUrl);
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const data = await resp.json();
            if (data.error) throw new Error(data.error);
            if (isLocal && data.aggregateStats?.workers) {
                const normalizedWallet = walletAddress.trim().toLowerCase();
                data.aggregateStats.workers = data.aggregateStats.workers.filter(worker => (
                    typeof worker.wallet === 'string' && worker.wallet.trim().toLowerCase() === normalizedWallet
                ));
            }
            setKaspaFetchTime(Date.now());
            setKaspaData(data);
        } catch (e) {
            setKaspaError(e.message);
            setKaspaData(null);
        }
    };

    // Countdown timer effect
    useEffect(() => {
        const timer = setInterval(() => {
            setTimeUntilRefresh(prev => prev > 0 ? prev - 1 : REFRESH_INTERVAL);
        }, 1000);
        
        return () => clearInterval(timer);
    }, []);

    // Handle window resize for mobile detection
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const formatNumber = (num, decimals = 2) => {
        if (num === undefined || num === null) return '0';
        return Number(num).toLocaleString(undefined, { 
            minimumFractionDigits: decimals, 
            maximumFractionDigits: decimals 
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear().toString().slice(-2);
        return `${day}-${month}-${year}`;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear().toString().slice(-2);
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day}-${month}-${year} ${hours}:${minutes}`;
    };

    // Get expected hashrate threshold based on worker name
    const getExpectedHashrate = (workerName) => {
        const name = workerName.toLowerCase();
        if (name.includes('ks7')) return 30;
        if (name.includes('ks5pro')) return 22.5;
        if (name.includes('ks5m')) return 15;
        if (name.includes('ks5l')) return 12.2;
        if (name.includes('ks0')) return 0.16; // 160 GH/s in TH/s
        return 0; // No threshold for unknown workers
    };

    // Get hashrates with non-zero values first
    const getHashrates = () => {
        if (!userData?.hashrate) return [];
        return Object.entries(userData.hashrate)
            .map(([algorithm, data]) => ({
                algorithm,
                hashrate: data.hashrate,
                units: data.hashrate_units,
                avgHashrate: data.hashrate_avg,
                avgUnits: data.hashrate_avg_units,
                estimatedRevenue: data.estimated_24h_usd_revenue
            }))
            .filter(h => h.hashrate > 0)
            .sort((a, b) => b.estimatedRevenue - a.estimatedRevenue);
    };

    // Get all workers from all algorithms
    const getAllWorkers = () => {
        if (!userData?.workers) return [];
        const allWorkers = [];
        Object.entries(userData.workers).forEach(([algorithm, workers]) => {
            if (Array.isArray(workers)) {
                workers.forEach(worker => {
                    const totalShares = worker.valid_shares + worker.invalid_shares + worker.stale_shares;
                    const rejectedPct = totalShares > 0 
                        ? ((worker.invalid_shares + worker.stale_shares) / totalShares) * 100 
                        : 0;
                    allWorkers.push({
                        ...worker,
                        rejectedPercentage: rejectedPct
                    });
                });
            }
        });
        return allWorkers.sort((a, b) => a.name.localeCompare(b.name));
    };

    // Get non-zero balances
    const getNonZeroBalances = () => {
        if (!userData?.balances) return [];
        return userData.balances.filter(b => b.balance > 0);
    };

    // Get earnings from all algorithms
    const getAllEarnings = () => {
        if (!userData?.earnings?.kheavyhash) return [];
        const filtered = userData.earnings.kheavyhash.filter(earning => {
            const date = new Date(earning.earning_timestamp);
            return !isNaN(date.getTime());
        });
        const mapped = filtered.map(earning => ({
            ...earning,
            earnings: earning.coins[0].coin_balance
        }));
        const sorted = mapped.sort((a, b) => new Date(b.earning_timestamp) - new Date(a.earning_timestamp));
        return sorted;
    };

    function usdToGbp(usd) {
        return usd * (usdToGbpRate || 0.79);
    }

    // Move finance tab options state to top-level of Mine component
    const [financeViewType, setFinanceViewType] = React.useState('Standard');
    const [financePriceType, setFinancePriceType] = React.useState('Current');

    if (error) {
        return (
            <div className="mine-dashboard" style={styles.container}>
                <article className="message is-danger">
                    <div className="message-header">
                        <p>Error Loading Data</p>
                    </div>
                    <div className="message-body">
                        <p>{error}</p>
                        <p className="mt-2">Please check your API key and try again.</p>
                    </div>
                </article>
            </div>
        );
    }

    const hashrates = getHashrates();
    // All kheavyhash workers for finance tab (including DateEnded)
    const workersAll = getAllWorkers().filter(w => w.algorithm === 'kheavyhash');
    // Only kheavyhash workers for dashboard (exclude DateEnded)
    const sha256NotActive = userData?.hashrate?.sha256?.hashrate === 0;
    const workers = getAllWorkers().filter(w => {
        if (w.algorithm === 'sha256' && w.hashrate === 0) return false;
        if (!workerFinance) return true;
        const rawName = w.name.replace('jjphotos.', '');
        let matchName = rawName;
        if (rawName.startsWith('ks0')) {
            const suffix = rawName.slice(3);
            if (suffix === 'Neville') matchName = 'ks02';
            else if (suffix === 'Irwin') matchName = 'ks03';
            else if (suffix === 'Ronaldo') matchName = 'ks07';
        }
        const fin = workerFinance.find(wf => wf.Name === matchName);
        return !fin?.DateEnded;
    });
    const balances = getNonZeroBalances();
    const earnings = getAllEarnings();
    const activeWorkers = workers.filter(w =>
        (w.algorithm === 'kheavyhash' && w.hashrate > 0) ||
        (w.algorithm === 'sha256' && w.hashrate > 0)
    ).length;

    let totalColor = '#22c55e'; // green
    if (activeWorkers < workers.length) {
        totalColor = '#ef4444'; // red
    } else {
        for (const worker of workers) {
            if (worker.hashrate > 0) {
                let hashrateTH = worker.hashrate;
                if (worker.name.toLowerCase().includes('ks0') && worker.hashrate_units?.includes('GH')) {
                    hashrateTH = worker.hashrate / 1000;
                }
                const expected = getExpectedHashrate(worker.name);
                if (hashrateTH < 0.5 * expected) {
                    totalColor = '#f59e42'; // orange
                    break;
                }
            }
        }
    }

    return (
        <div className="mine-dashboard" style={styles.container}>
            <div className="container">
                {loading && initialLoad && (
                    <p role="status" style={{ color: '#94a3b8', marginBottom: '1rem' }}>Loading mining data…</p>
                )}
                {/* Header Row: Tabs only */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <div style={{ flex: 1 }}>
                        <div className="tabs is-centered" style={{ marginBottom: 0 }}>
                            <ul>
                                <li className={activeTab === 'dashboard' ? 'is-active' : ''}>
                                    <a
                                        href="#dashboard"
                                        onClick={event => { event.preventDefault(); setActiveTab('dashboard'); }}
                                        aria-current={activeTab === 'dashboard' ? 'page' : undefined}
                                    >
                                        Dashboard
                                    </a>
                                </li>
                                <li className={activeTab === 'details' ? 'is-active' : ''}>
                                    <a
                                        href="#details"
                                        onClick={event => { event.preventDefault(); setActiveTab('details'); }}
                                        aria-current={activeTab === 'details' ? 'page' : undefined}
                                    >
                                        Details
                                    </a>
                                </li>
                                {false && <li className={activeTab === 'finance' ? 'is-active' : ''}>
                                    <a onClick={() => setActiveTab('finance')}>
                                        {/* Finance icon: Dollar/Currency */}
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{verticalAlign: 'middle', marginRight: 6, color: '#f59e42'}}><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/><path d="M12 8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M15 10c0-1.105-1.343-2-3-2s-3 .895-3 2c0 1.105 1.343 2 3 2s3 .895 3 2c0 1.105-1.343 2-3 2s-3-.895-3-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                                        Finance
                                    </a>
                                </li>}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Dashboard Tab Content */}
                {activeTab === 'dashboard' && (
                                    <DashboardTab
                                        userData={userData}
                                        workerFinance={workerFinance}
                                        poolData={poolData}
                                        k1PoolData={k1PoolData}
                                        hashrates={hashrates}
                                        workers={workers}
                                        balances={balances}
                                        earnings={earnings}
                                        activeWorkers={activeWorkers}
                                        totalColor={totalColor}
                                        isMobile={isMobile}
                                        kasPrice={kasPrice}
                                        priceLoading={priceLoading}
                                        priceError={priceError}
                                        miningTotal={miningTotal}
                                        miningBalanceLoading={miningBalanceLoading}
                                        miningBalanceError={miningBalanceError}
                                        ledgerTotal={ledgerTotal}
                                        styles={styles}
                                        formatNumber={formatNumber}
                                        getExpectedHashrate={getExpectedHashrate}
                                        timeUntilRefresh={timeUntilRefresh}
                                        formatTime={formatTime}
                                        kasChange24h={kasChange24h}
                                        kaspaData={kaspaData}
                                        kaspaError={kaspaError}
                                        kaspaFetchTime={kaspaFetchTime}
                                        usdcMiningBalance={(() => {
                                            const val = balanceHistory.length > 0 ? balanceHistory[balanceHistory.length - 1]?.usdcBalance : null;
                                            //console.log('DEBUG: Passing usdcMiningBalance to DashboardTab:', val, 'balanceHistory length:', balanceHistory.length);
                                            return val;
                                        })()}
                                    />
                )}

                {/* Details Tab Content (moved to DetailsTab.js) */}
                {activeTab === 'details' && (
                    <>
                        <DetailsTab
                            kasPriceHistory={kasPriceHistory}
                            pricePeriod={pricePeriod}
                            setPricePeriod={setPricePeriod}
                            networkHashrateHistory={networkHashrateHistory}
                            period={period}
                            setPeriod={setPeriod}
                            earnings={earnings}
                            hashrates={hashrates}
                            workers={workers}
                            activeWorkers={activeWorkers}
                            userData={userData}
                            workerFinance={workerFinance}
                            totalColor={totalColor}
                            isMobile={isMobile}
                            styles={styles}
                            formatNumber={formatNumber}
                            formatDateTime={formatDateTime}
                            formatDate={formatDate}
                            balanceHistory={balanceHistory}
                            balanceHistoryLoading={balanceHistoryLoading}
                            balanceHistoryError={balanceHistoryError}
                            kasAddedHistory={kasAddedHistory}
                            kasAddedLoading={kasAddedLoading}
                            kasAddedError={kasAddedError}
                            poolData={poolData}
                            k1PoolData={k1PoolData}
                            kasPrice={kasPrice}
                            priceLoading={priceLoading}
                            priceError={priceError}
                            miningTotal={miningTotal}
                            miningBalanceLoading={miningBalanceLoading}
                            miningBalanceError={miningBalanceError}
                            ledgerTotal={ledgerTotal}
                            getExpectedHashrate={getExpectedHashrate}
                            timeUntilRefresh={timeUntilRefresh}
                            formatTime={formatTime}
                            kasChange24h={kasChange24h}
                            kasHigh24h={kasHigh24h}
                            kasLow24h={kasLow24h}
                            kaspaData={kaspaData}
                            kaspaError={kaspaError}
                            kaspaFetchTime={kaspaFetchTime}
                            usdcMiningBalance={(() => {
                                const val = balanceHistory.length > 0 ? balanceHistory[balanceHistory.length - 1]?.usdcBalance : null;
                                //console.log('DEBUG: Passing usdcMiningBalance to DashboardTab:', val, 'balanceHistory length:', balanceHistory.length);
                                return val;
                            })()}
                        />
                    </>
                )}


                {/* Kheavyhash Workers Table: Only show in finance tab */}
                {activeTab === 'finance' && (
                    <FinanceTab
                        outstandingPowerpoolBalance={outstandingPowerpoolBalance}
                        kasPriceHistory={kasPriceHistory}
                        pricePeriod={pricePeriod}
                        setPricePeriod={setPricePeriod}
                        networkHashrateHistory={networkHashrateHistory}
                        period={period}
                        setPeriod={setPeriod}
                        earnings={earnings}
                        hashrates={hashrates}
                        workers={workers}
                        activeWorkers={activeWorkers}
                        userData={userData}
                        workerFinance={workerFinance}
                        totalColor={totalColor}
                        isMobile={isMobile}
                        styles={styles}
                        formatNumber={formatNumber}
                        formatDateTime={formatDateTime}
                        formatDate={formatDate}
                        balanceHistory={balanceHistory}
                        poolData={poolData}
                        k1PoolData={k1PoolData}
                        kasPrice={kasPrice}
                        priceLoading={priceLoading}
                        priceError={priceError}
                        miningTotal={miningTotal}
                        ledgerTotal={ledgerTotal}
                        getExpectedHashrate={getExpectedHashrate}
                        timeUntilRefresh={timeUntilRefresh}
                        formatTime={formatTime}
                        financeViewType={financeViewType}
                        setFinanceViewType={setFinanceViewType}
                        financePriceType={financePriceType}
                        setFinancePriceType={setFinancePriceType}
                        usdToGbpRate={usdToGbpRate}
                        getAllWorkers={getAllWorkers}
                        usdToGbp={usdToGbp}
                        balances={userData?.balances || []}
                        kasChange24h={kasChange24h}
                        usdcMiningBalance={(() => {
                            const val = balanceHistory.length > 0 ? balanceHistory[balanceHistory.length - 1]?.usdcBalance : null;
                           // console.log('DEBUG: Passing usdcMiningBalance to DashboardTab:', val, 'balanceHistory length:', balanceHistory.length);
                            return val;
                        })()}
                    />
                )}
                

                {/* Raw Data Debug */}
                {userData && (
                    <details className="mt-5">
                        <summary style={styles.debugSummary}>View Raw API Response</summary>
                        <pre style={styles.debugPre}>
                            {JSON.stringify(userData, null, 2)}
                        </pre>
                    </details>
                )}
            </div>
        </div>
    );
};

const styles = {
    container: {
        backgroundColor: '#0f172a',
        minHeight: '100vh',
        padding: '0 1.5rem 5rem 1.5rem',
        color: '#e2e8f0'
    },
    headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        padding: '0.75rem 1rem',
        backgroundColor: '#1e293b',
        borderRadius: '8px',
        border: '1px solid #334155'
    },
    timerText: {
        color: '#94a3b8',
        fontSize: '0.875rem'
    },
    powerpoolButton: {
        textDecoration: 'none'
    },
    chartBox: {
        backgroundColor: '#1e293b',
        border: '1px solid #334155',
        borderRadius: '12px',
        padding: '1.25rem'
    },
    loading: {
        paddingTop: '4rem',
        color: '#94a3b8'
    },
    statCard: {
        backgroundColor: '#1e293b',
        border: '1px solid #334155',
        borderRadius: '12px'
    },
    statLabel: {
        color: '#94a3b8',
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
    },
    statSubLabel: {
        color: '#64748b',
        fontSize: '0.875rem',
        marginTop: '0.25rem'
    },
    revenueLabel: {
        color: '#22c55e',
        fontSize: '0.875rem',
        fontWeight: '500',
        marginTop: '0.5rem'
    },
    poolLabel: {
        color: '#94a3b8',
        fontSize: '0.8rem',
        marginTop: '0.5rem'
    },
    balanceLabel: {
        color: '#60a5fa',
        fontSize: '0.875rem',
        fontWeight: '500',
        marginTop: '0.5rem'
    },
    statValue: {
        color: '#f1f5f9',
        fontSize: '1.5rem',
        fontWeight: '600'
    },
    statSubtext: {
        color: '#64748b',
        fontSize: '1rem',
        fontWeight: '400'
    },
    coinLabel: {
        color: '#94a3b8',
        fontSize: '0.875rem',
        fontWeight: '400'
    },
    tableBox: {
        backgroundColor: '#1e293b',
        border: '1px solid #334155',
        borderRadius: '12px'
    },
    tableHeader_row: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0.5rem'
    },
    checkboxLabel: {
        color: '#94a3b8',
        fontSize: '0.875rem',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
    },
    checkbox: {
        cursor: 'pointer'
    },
    tableTitle: {
        color: '#f1f5f9'
    },
    table: {
        backgroundColor: 'transparent'
    },
    tableHeader: {
        color: '#94a3b8',
        borderColor: '#334155',
        backgroundColor: 'transparent'
    },
    tableCell: {
        color: '#e2e8f0',
        borderColor: '#334155',
        backgroundColor: 'transparent'
    },
    txLink: {
        color: '#60a5fa',
        textDecoration: 'none'
    },
    debugSummary: {
        color: '#64748b',
        cursor: 'pointer',
        padding: '0.5rem'
    },
    debugPre: {
        backgroundColor: '#1e293b',
        color: '#94a3b8',
        padding: '1rem',
        borderRadius: '8px',
        overflow: 'auto',
        fontSize: '0.75rem'
    }
};

export default Mine;
